import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

// Definição das perguntas para evitar re-renderizações desnecessárias
const faqItems = [
  {
    id: 'item-1',
    questionPtBr: 'O que é um arquivo ZPL?',
    questionEn: 'What is a ZPL file?',
    answerPtBr: 'ZPL (Zebra Programming Language) é uma linguagem usada para gerar etiquetas em impressoras Zebra. O conteúdo do arquivo ZPL define a estrutura da etiqueta (texto, QR code, códigos de barras, etc).',
    answerEn: 'ZPL (Zebra Programming Language) is a language used to generate labels for Zebra printers. The ZPL file content defines the label structure (text, QR codes, barcodes, etc).'
  },
  {
    id: 'item-2',
    questionPtBr: 'Posso usar o ZPL Easy sem impressora?',
    questionEn: 'Can I use ZPL Easy without a printer?',
    answerPtBr: 'Sim! O ZPL Easy é ideal para visualizar e testar etiquetas sem precisar da impressora física.',
    answerEn: 'Yes! ZPL Easy is ideal for viewing and testing labels without needing a physical printer.'
  },
  {
    id: 'item-3',
    questionPtBr: 'Funciona com etiquetas da Shopee ou Mercado Livre?',
    questionEn: 'Does it work with Shopee or Mercado Livre labels?',
    answerPtBr: 'Sim. Basta exportar o arquivo .txt com o código ZPL gerado pelo marketplace e fazer o upload no ZPL Easy para visualizar ou converter em PDF.',
    answerEn: 'Yes. Just export the .txt file with the ZPL code generated by the marketplace and upload it to ZPL Easy to view or convert to PDF.'
  },
  {
    id: 'item-4',
    questionPtBr: 'O ZPL Easy é gratuito?',
    questionEn: 'Is ZPL Easy free?',
    answerPtBr: 'Sim! O ZPL Easy é 100% gratuito. Mantemos a ferramenta através de doações voluntárias de usuários que desejam apoiar o projeto.',
    answerEn: 'Yes! ZPL Easy is 100% free. We keep the tool running through voluntary donations from users who want to support the project.'
  },
  {
    id: 'item-5',
    questionPtBr: 'Qual a diferença entre o formato Standard e HD?',
    questionEn: 'What is the difference between Standard and HD format?',
    answerPtBr: 'O formato Standard gera as etiquetas rapidamente. O formato HD (Alta Qualidade) utiliza inteligência artificial para melhorar a qualidade de cada etiqueta, resultando em impressões mais nítidas. O processo HD leva mais tempo, mas oferece resultados superiores.',
    answerEn: 'Standard format generates labels quickly. HD (High Quality) format uses artificial intelligence to enhance each label\'s quality, resulting in sharper prints. The HD process takes longer but offers superior results.'
  },
  {
    id: 'item-6',
    questionPtBr: 'Posso fazer upload de múltiplos arquivos ao mesmo tempo?',
    questionEn: 'Can I upload multiple files at once?',
    answerPtBr: 'Sim! Você pode enviar vários arquivos TXT ou ZPL de uma só vez, ou ainda um arquivo ZIP contendo múltiplos arquivos. Todos serão processados e combinados em um único PDF.',
    answerEn: 'Yes! You can upload multiple TXT or ZPL files at once, or even a ZIP file containing multiple files. All will be processed and combined into a single PDF.'
  },
  {
    id: 'item-7',
    questionPtBr: 'Quais formatos de arquivo posso enviar?',
    questionEn: 'What file formats can I upload?',
    answerPtBr: 'Aceitamos arquivos .txt contendo código ZPL, arquivos .zpl e arquivos .zip com múltiplos arquivos ZPL. Você também pode enviar PDFs com etiquetas para conversão.',
    answerEn: 'We accept .txt files containing ZPL code, .zpl files, and .zip files with multiple ZPL files. You can also upload PDFs with labels for conversion.'
  },
  {
    id: 'item-8',
    questionPtBr: 'Como funciona o download em ZIP?',
    questionEn: 'How does the ZIP download work?',
    answerPtBr: 'Após a conversão, você pode baixar todas as etiquetas em um único arquivo ZIP, facilitando o armazenamento e organização. Ideal para quem processa grandes volumes de etiquetas.',
    answerEn: 'After conversion, you can download all labels in a single ZIP file, making storage and organization easier. Ideal for those who process large volumes of labels.'
  },
  {
    id: 'item-9',
    questionPtBr: 'Por quanto tempo minhas etiquetas ficam salvas?',
    questionEn: 'How long are my labels stored?',
    answerPtBr: 'Os registros e arquivos são mantidos no histórico por até 60 dias. Após esse período, são excluídos automaticamente para garantir a limpeza do sistema.',
    answerEn: 'Records and files are kept in history for up to 60 days. After this period, they are automatically deleted to ensure system cleanliness.'
  },
  {
    id: 'item-10',
    questionPtBr: 'Meus dados estão seguros?',
    questionEn: 'Is my data secure?',
    answerPtBr: 'Sim! Utilizamos conexões seguras (HTTPS) e seus arquivos são processados de forma isolada. Não compartilhamos seus dados com terceiros e você pode excluir seu histórico a qualquer momento.',
    answerEn: 'Yes! We use secure connections (HTTPS) and your files are processed in isolation. We don\'t share your data with third parties and you can delete your history at any time.'
  },
  {
    id: 'item-11',
    questionPtBr: 'Posso acessar meu histórico de conversões?',
    questionEn: 'Can I access my conversion history?',
    answerPtBr: 'Sim! Após fazer login, você tem acesso completo ao seu histórico de processamento, podendo visualizar, baixar novamente ou excluir etiquetas convertidas anteriormente.',
    answerEn: 'Yes! After logging in, you have full access to your processing history, where you can view, re-download, or delete previously converted labels.'
  },
  {
    id: 'item-12',
    questionPtBr: 'Como posso enviar feedback ou reportar um problema?',
    questionEn: 'How can I send feedback or report a problem?',
    answerPtBr: 'Você pode usar o botão "Feedback" disponível no aplicativo para enviar sugestões, reportar bugs ou fazer reclamações. Respondemos o mais rápido possível!',
    answerEn: 'You can use the "Feedback" button available in the app to send suggestions, report bugs, or make complaints. We respond as quickly as possible!'
  },
  {
    id: 'item-13',
    questionPtBr: 'A plataforma funciona em outros idiomas?',
    questionEn: 'Does the platform work in other languages?',
    answerPtBr: 'Sim! O ZPL Easy está disponível em Português e Inglês. Você pode alternar entre os idiomas usando o seletor de idioma na interface.',
    answerEn: 'Yes! ZPL Easy is available in Portuguese and English. You can switch between languages using the language selector in the interface.'
  },
  {
    id: 'item-14',
    questionPtBr: 'Como posso apoiar o projeto?',
    questionEn: 'How can I support the project?',
    answerPtBr: 'Você pode nos apoiar com uma doação via PIX ou cartão de crédito. Cada contribuição ajuda a manter o ZPL Easy gratuito e em constante evolução!',
    answerEn: 'You can support us with a donation via PIX or credit card. Every contribution helps keep ZPL Easy free and constantly evolving!'
  },
  {
    id: 'item-15',
    questionPtBr: 'Quais plataformas de marketplace e e-commerce utilizam etiquetas ZPL?',
    questionEn: 'Which marketplace and e-commerce platforms use ZPL labels?',
    answerPtBr: 'Muitas plataformas populares utilizam ZPL: Mercado Livre, Shopee, Amazon (FBA/FBM), Shopify, Nuvemshop, Loja Integrada, WooCommerce, Magento, PrestaShop, além de gateways logísticos como Melhor Envio, Frenet, Intelipost, Kangu, Total Express, Loggi e Correios.',
    answerEn: 'Many popular platforms use ZPL: Mercado Livre, Shopee, Amazon (FBA/FBM), Shopify, Nuvemshop, Loja Integrada, WooCommerce, Magento, PrestaShop, as well as logistics gateways like Melhor Envio, Frenet, Intelipost, Kangu, Total Express, Loggi, and Correios.'
  },
  {
    id: 'item-16',
    questionPtBr: 'Minha impressora térmica Zebra é compatível?',
    questionEn: 'Is my Zebra thermal printer compatible?',
    answerPtBr: 'Sim! Toda impressora que suporte o padrão ZPL II (como GK420t, ZD220, ZT230, entre outros) é compatível. Os PDFs gerados são otimizados para resolução de 203 DPI.',
    answerEn: 'Yes! Any printer that supports the ZPL II standard (such as GK420t, ZD220, ZT230, among others) is compatible. Generated PDFs are optimized for 203 DPI resolution.'
  }
];

// Componente FAQ otimizado com memo para evitar renderizações desnecessárias
const FAQItem = memo(({ 
  item, 
  isPortuguese 
}: { 
  item: typeof faqItems[0], 
  isPortuguese: boolean 
}) => (
  <AccordionItem value={item.id}>
    <AccordionTrigger>
      {isPortuguese ? item.questionPtBr : item.questionEn}
    </AccordionTrigger>
    <AccordionContent>
      {isPortuguese ? item.answerPtBr : item.answerEn}
    </AccordionContent>
  </AccordionItem>
));

// Componente principal com nome mais específico para melhor profiling
export const FAQSection: React.FC = () => {
  const { i18n } = useTranslation();
  const isPortuguese = i18n.language === 'pt-BR';
  
  return (
    <section className="py-20 bg-white dark:bg-gray-900" id="faq">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
          {isPortuguese ? 'Perguntas Frequentes' : 'Frequently Asked Questions'}
        </h2>
        
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map(item => (
              <FAQItem 
                key={item.id}
                item={item}
                isPortuguese={isPortuguese}
              />
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

// Export faqItems for reuse in FAQ page
export { faqItems };
